import {
  Component,
  computed,
  inject,
  input,
  output,
  OutputEmitterRef,
  signal,
  WritableSignal,
} from '@angular/core';

import {SidePanelGridComponent} from '../../../../shared/ui/side-panel-grid/side-panel-grid.component';
import {translate, TranslocoPipe} from '@jsverse/transloco';
import {InquiryService} from '../../service/inquiry.service';
import {ButtonType, CovButtonComponent, CovToastService} from '@cov/ui-angular';
import {PageId} from '../../enum/inquiry-page.enum';
import {InquiryOrderInfoComponent} from '../inquiry-order-info/inquiry-order-info.component';
import {ExpandableComponent} from '../../../../shared/ui/expandable/expandable.component';
import {RemoveLeadingZerosPipe} from '../../../../shared/pipes/removeLeadingZeros.pipe';
import {FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {MatFormField, MatInput} from '@angular/material/input';
import {take, tap} from 'rxjs';
import {DragDropFileSelectComponent} from '../../../../shared/ui/drag-drop-file-select/drag-drop-file-select.component';
import {AbstractBlendingPageComponent} from '../abstract-blending-page/abstract-blending-page.component';
import {
  CustomerSalesRepresentativeInfo,
  InquiryOrder,
} from '../../data-access/interface/inquiry.interface';
import {InquiryAttachedFilesComponent} from '../inquiry-attached-files/inquiry-attached-files.component';
import {validateAllFormFields} from '../../../../shared/utils/form.utils';
import {add} from 'date-fns/add';
import {SoldToAddress} from '../../../../interfaces/sold-to-ship-to.interface';
import {DragDropFileSelectError} from '../../../../shared/enum/drag-drop-error.enum';
import {formatFileSize} from '../../../../shared/utils/file.utils';
import {MimeType} from '../../../../shared/enum/mime-type.enum';
import {MatProgressSpinner} from '@angular/material/progress-spinner';
import {AsmService} from '../../../../shared/services/asm.service';
import {Permission} from '../../../../enums/permissions.enum';

@Component({
  selector: 'cov-inquiry-contact-panel',
  imports: [
    SidePanelGridComponent,
    TranslocoPipe,
    CovButtonComponent,
    InquiryOrderInfoComponent,
    ExpandableComponent,
    RemoveLeadingZerosPipe,
    ReactiveFormsModule,
    MatFormField,
    MatInput,
    DragDropFileSelectComponent,
    InquiryAttachedFilesComponent,
    MatProgressSpinner,
  ],
  templateUrl: './inquiry-contact-panel.component.html',
  styleUrl: './inquiry-contact-panel.component.scss',
})
export class InquiryContactPanelComponent extends AbstractBlendingPageComponent {
  protected inquiryService = inject(InquiryService);
  protected asmService = inject(AsmService);

  // CONST
  protected readonly PageId = PageId;
  protected readonly translate = translate;
  protected readonly ButtonType = ButtonType;

  private readonly MAX_ATTACHMENTS_COUNT = 5;
  protected readonly MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
  protected readonly ALLOWED_FORMATS = [
    MimeType.PDF,
    MimeType.JPEG_IMAGE,
    MimeType.DOC,
    MimeType.DOCX,
    MimeType.XLS,
    MimeType.CSV,
    MimeType.XLSX,
  ];
  protected readonly ALLOWED_FORMATS_STRING = '.pdf, .jpg, .doc, .xls, .csv';
  protected readonly ALLOWED_FILE_EXTENSIONS = '.pdf, .jpg, .doc, .docx, .xls, .xlsx, .csv';

  // INJECTS
  private fb: FormBuilder = inject(FormBuilder);
  private toastService: CovToastService = inject(CovToastService);

  // INPUT
  public order = input<InquiryOrder | null>(null);
  public soldTos = input<SoldToAddress[]>([]);
  public contactEmail = input<string>();

  // OUTPUT
  public cancelInquiry: OutputEmitterRef<PageId> = output();
  public inquiryCreated: OutputEmitterRef<void> = output();

  // STATE
  protected hasOrder = computed(() => this.order() !== null);
  protected attachedFiles: WritableSignal<File[]> = signal([]);
  protected form: WritableSignal<FormGroup | null> = signal(null);
  protected csrInfos: WritableSignal<CustomerSalesRepresentativeInfo[]> = signal([]);
  disableSendButton = computed(
    () =>
      this.inquiryService.inquirySubmitLoading() ||
      this.asmService.isAsmAgentAndDoesNotHavePermissionsSignal([
        Permission.ASM_INQUIRIES_CREATE,
      ])(),
  );

  private loadCsrInfos(): void {
    this.inquiryService
      .loadCsrInformation()
      .pipe(
        tap((list: CustomerSalesRepresentativeInfo[]) => {
          this.csrInfos.set(list);
          if (list.length === 1) {
            this.csrEmailControl?.setValue(list[0].mail);
          }
          if (this.contactEmail()) {
            const foundContact = list.find((entry) => entry.mail === this.contactEmail());
            this.csrEmailControl.setValue(foundContact?.mail);
          }
        }),
        take(1),
      )
      .subscribe();
  }

  private initForm(): void {
    const form = this.hasOrder()
      ? this.generateOrderInquiryForm()
      : this.generateGeneralInquiryForm();
    this.form.set(form);
  }

  private generateGeneralInquiryForm(): FormGroup {
    const singleSoldTo = this.soldTos().length === 1;

    return this.fb.group(
      {
        message: ['', [Validators.required]],
        csrEmail: ['', {validators: [Validators.required]}],
        soldTo: [singleSoldTo ? this.soldTos()[0].code : '', Validators.required],
      },
      {updateOn: 'blur'},
    );
  }

  private generateOrderInquiryForm(): FormGroup {
    return this.fb.group(
      {
        message: ['', [Validators.required]],
      },
      {updateOn: 'blur'},
    );
  }

  public get csrEmailControl(): FormControl {
    return this.form()?.get('csrEmail') as FormControl;
  }

  public get soldToControl(): FormControl {
    return this.form()?.get('soldTo') as FormControl;
  }

  public onFileError(error: DragDropFileSelectError): void {
    switch (error) {
      case DragDropFileSelectError.MAX_FILE_SIZE:
        this.toastService.error(
          translate('inquiries.sideDrawer.fileAttachments.notifications.maxFileSize', {
            maxFileSize: formatFileSize(this.MAX_FILE_SIZE),
          }),
        );
        break;
      case DragDropFileSelectError.UN_SUPPORTED_FORMAT:
        this.toastService.error(
          translate('inquiries.sideDrawer.fileAttachments.notifications.notSupportedFormat'),
        );
        break;
    }
  }

  public addFiles(fileList: FileList): void {
    if (this.attachedFiles().length >= this.MAX_ATTACHMENTS_COUNT) {
      this.toastService.info(
        translate('inquiries.sideDrawer.fileAttachments.notifications.maxAttachmentReached'),
      );
      return;
    }

    const newFiles = Array.from(fileList);
    const realNewFiles = newFiles.filter((newFile) => !this.isFileAttached(newFile));
    if (newFiles.length !== realNewFiles.length) {
      this.toastService.info(
        translate('inquiries.sideDrawer.fileAttachments.notifications.alreadyAttached'),
      );
    }
    const newList = [...this.attachedFiles(), ...realNewFiles];
    this.attachedFiles.set(newList);
  }

  public submit(): void {
    validateAllFormFields(this.form());
    if (!this.form()?.valid) {
      return;
    }
    this.sendInquiry();
  }

  private isFileAttached(file: File): boolean {
    return !!this.attachedFiles().find((f) => f.name === file.name && f.size === file.size);
  }

  private sendInquiry(): void {
    const form = this.form()?.getRawValue();
    if (form && form.message) {
      const order = this.order();
      if (order) {
        this.inquiryService
          .createOrderInquiry(order, form.message, this.attachedFiles())
          .subscribe(() => {
            this.inquiryCreated.emit();
          });
      } else {
        this.inquiryService
          .createGeneralInquiry(form.message, form.csrEmail, form.soldTo)
          .subscribe(() => {
            this.inquiryCreated.emit();
          });
      }
    }
  }

  public deleteFile(file: File): void {
    const newList = this.attachedFiles().filter(
      (currFile) => currFile.name !== file.name && currFile.size !== file.size,
    );
    this.attachedFiles.set(newList);
  }

  public override onActivated(): void {
    this.initForm();
    this.loadCsrInfos();
  }

  public override onDeactivated() {
    this.attachedFiles.set([]);
  }

  protected readonly add = add;
}
